import {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
  type ReactNode,
} from 'react';
import type { User, Flight, Friendship, RosterEntry, Overlap, Message, GroupChat, GroupMember, GroupMessage, ConversationDeletion, MeetupProposalMetadata, GroupMessageMetadata, ImageInput, Meetup, MeetupRsvp } from '../types';
import type { Achievement } from '../utils/achievements';
import type { AddFlightsResult } from '../services/supabaseService';
import * as sb from '../services/supabaseService';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';
import { buildRoster } from '../utils/roster';
import { calculateOverlaps } from '../utils/overlaps';
import { hasActiveRoster, isMonthInPast } from '../utils/rosterGating';
import { isPremiumActive, isOverlapGatedByPremium } from '../utils/premiumGating';
import { computeFlightStats } from '../utils/flightStats';
import { computeAchievements } from '../utils/achievements';
import { saveAppData, loadAppData, queueFriendRequest, getFriendRequestOutbox, clearFriendRequestOutbox } from '../lib/offlineCache';
import { scheduleOverlapNotifications, registerOverlapNotificationListener } from '../services/overlapNotificationScheduler';
import {
  getMutedFriends,
  toggleFriendMute as toggleMute,
} from '../services/notificationStore';
import { useOverlapNotifications, type ToastData } from '../hooks/useOverlapNotifications';
import { setupRosterReminder, registerReminderListener, markRosterUploaded } from '../services/rosterReminder';
import { initShareExtensionListener, onSharedRosterText, onSharedRosterImage } from '../services/shareExtension';
import { initVisitMonitor } from '../services/visitMonitor';
import { initPushNotifications } from '../services/pushNotificationService';
import { track } from '../analytics';

function requireOnline(action: string): boolean {
  if (navigator.onLine) return true;
  window.dispatchEvent(new CustomEvent('offline-mutation', { detail: { action } }));
  return false;
}

const LOAD_TIMEOUT_MS = 5_000;

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('Network timeout')), ms);
    promise.then(
      (v) => { clearTimeout(timer); resolve(v); },
      (e) => { clearTimeout(timer); reject(e); },
    );
  });
}

/** After roster import, notify friends who have overlapping flights. */
async function notifyFriendsOfOverlaps(userId: string, insertedFlights: Flight[]): Promise<void> {
  try {
    // Get the cities+dates from newly inserted flights (arrivals = layover cities)
    const newCityDates = new Map<string, Set<string>>(); // city → set of dates
    for (const f of insertedFlights) {
      const city = f.arrivalAirport;
      if (!city) continue;
      if (!newCityDates.has(city)) newCityDates.set(city, new Set());
      newCityDates.get(city)!.add(f.date);
      // Also count departure city (for return flights)
      if (f.departureAirport && f.departureAirport !== city) {
        if (!newCityDates.has(f.departureAirport)) newCityDates.set(f.departureAirport, new Set());
        newCityDates.get(f.departureAirport)!.add(f.date);
      }
    }
    if (newCityDates.size === 0) return;

    // Get accepted friends
    const { data: friendships } = await supabase
      .from('friendships')
      .select('from_user_id, to_user_id')
      .or(`from_user_id.eq.${userId},to_user_id.eq.${userId}`)
      .eq('status' as never, 'accepted');

    if (!friendships?.length) return;

    const friendIds = friendships.map((f) =>
      f.from_user_id === userId ? f.to_user_id : f.from_user_id
    );

    // Get friends' flights in the same date range
    const allDates = [...new Set([...newCityDates.values()].flatMap((s) => [...s]))];
    const minDate = allDates.sort()[0];
    const maxDate = allDates.sort().reverse()[0];

    const { data: friendFlights } = await supabase
      .from('flights')
      .select('user_id, arrival_airport, departure_airport, date')
      .in('user_id', friendIds)
      .gte('date', minDate)
      .lte('date', maxDate);

    if (!friendFlights?.length) return;

    // Match: friend flight in same city on same date
    const friendOverlapMap = new Map<string, { cities: Set<string>; count: number }>();

    for (const ff of friendFlights) {
      const friendId = ff.user_id;
      const checkCities = [ff.arrival_airport, ff.departure_airport].filter(Boolean);

      for (const city of checkCities) {
        const myDates = newCityDates.get(city);
        if (myDates?.has(ff.date)) {
          if (!friendOverlapMap.has(friendId)) {
            friendOverlapMap.set(friendId, { cities: new Set(), count: 0 });
          }
          const entry = friendOverlapMap.get(friendId)!;
          entry.cities.add(city);
          entry.count++;
          break; // Don't double-count same flight
        }
      }
    }

    if (friendOverlapMap.size === 0) return;

    const friendOverlaps = [...friendOverlapMap.entries()].map(([friendId, data]) => ({
      friendId,
      overlapCount: data.cities.size, // count unique cities, not flight pairs
      cities: [...data.cities],
    }));

    await supabase.functions.invoke('notify-roster-overlaps', {
      body: { userId, friendOverlaps },
    });
  } catch (err) {
    console.warn('[AppContext] notifyFriendsOfOverlaps failed:', err);
  }
}

interface AppState {
  // Loading
  loading: boolean;

  // Offline cache
  cachedAt: string | null;

  // Current user
  currentUser: User;

  // Date navigation
  month: number;
  year: number;

  // Computed
  flights: Flight[];
  allFlights: Flight[];
  roster: RosterEntry[];
  friends: User[];
  friendships: Friendship[];
  pendingRequests: Friendship[];
  usersById: Map<string, User>;
  overlaps: Overlap[];

  // Messages
  messages: Message[];
  unreadMessageCount: number;
  sendMessage: (receiverId: string, content: string) => Promise<void>;
  editMessage: (messageId: string, content: string) => Promise<void>;
  deleteMessage: (messageId: string) => Promise<void>;
  markConversationRead: (partnerId: string) => Promise<void>;

  // Group chats
  groupChats: GroupChat[];
  groupMembers: GroupMember[];
  groupMessages: GroupMessage[];
  conversationDeletions: ConversationDeletion[];
  createGroupChat: (name: string, memberIds: string[], cityIata?: string, rangeStart?: string, rangeEnd?: string) => Promise<GroupChat | null>;
  sendGroupMessage: (groupId: string, content: string, metadata?: MeetupProposalMetadata) => Promise<void>;
  editGroupMessage: (messageId: string, content: string) => Promise<void>;
  deleteGroupMessage: (messageId: string) => Promise<void>;
  voteOnProposal: (messageId: string, vote: 'yes' | 'no') => Promise<void>;
  renameGroupChat: (groupId: string, name: string) => Promise<void>;
  softDeleteGroupChat: (groupId: string) => Promise<void>;
  softDeleteDmConversation: (partnerId: string) => Promise<void>;

  // Meetups
  meetups: Meetup[];
  meetupRsvps: MeetupRsvp[];
  createMeetup: (params: { groupChatId: string; city: string; date: string; time?: string; place?: string; activity?: string; messageId?: string; memberIds: string[] }) => Promise<Meetup | null>;
  rsvpMeetup: (meetupId: string, vote: 'yes' | 'no') => Promise<void>;
  cancelMeetup: (meetupId: string) => Promise<void>;

  // Roster gating
  rosterActive: boolean;

  // Premium
  isPremium: boolean;
  premiumGatedOverlap: (date: string) => boolean;

  // Referrals
  referralCount: number;

  // Achievement celebrations
  celebratingAchievement: Achievement | null;
  dismissCelebration: () => void;

  // Notifications
  unseenOverlapCount: number;
  mutedFriendIds: Set<string>;
  toggleFriendMute: (friendId: string) => void;
  markOverlapsSeen: () => void;
  toasts: ToastData[];
  dismissToast: (id: string) => void;

  // Share extension
  pendingSharedText: string | null;
  pendingSharedImage: ImageInput | null;
  clearPendingShare: () => void;

  // Error state
  loadError: boolean;

  // Actions
  setMonth: (month: number, year: number) => void;
  addFlight: (flight: Flight) => void;
  addFlights: (flights: Flight[]) => Promise<AddFlightsResult>;
  removeFlight: (flightId: string) => void;
  updateFlight: (flightId: string, updates: Partial<Omit<Flight, 'id' | 'userId'>>) => Promise<void>;
  sendFriendRequest: (toHandle: string) => Promise<string | null>;
  acceptFriendRequest: (friendshipId: string) => Promise<void>;
  rejectFriendRequest: (friendshipId: string) => Promise<void>;
  removeFriend: (friendUserId: string) => Promise<void>;
  refreshData: () => Promise<void>;
  updateCurrentUser: (updates: Partial<User>) => Promise<void>;
}

const AppContext = createContext<AppState | null>(null);

export function AppProvider({ children }: { children: ReactNode }) {
  const { user: authUser } = useAuth();
  const currentUserId = authUser?.id ?? '';

  // Load cached data for instant offline rendering
  const cached = useMemo(() => currentUserId ? loadAppData(currentUserId) : null, [currentUserId]);

  const [loading, setLoading] = useState(!cached);
  const [cachedAt, setCachedAt] = useState<string | null>(cached?.cachedAt ?? null);
  const [currentUser, setCurrentUser] = useState<User>(() => authUser ?? cached?.currentUser ?? { id: '', handle: '', name: '', airlineCode: '', baseAirport: '', tier: 'free' });
  const [flights, setFlights] = useState<Flight[]>(cached?.flights ?? []);
  const [allFlights, setAllFlights] = useState<Flight[]>(cached?.allFlights ?? []);
  const [friends, setFriends] = useState<User[]>(cached?.friends ?? []);
  const [friendships, setFriendships] = useState<Friendship[]>(cached?.friendships ?? []);
  const [pendingRequests, setPendingRequests] = useState<Friendship[]>(cached?.pendingRequests ?? []);
  const [usersById, setUsersById] = useState<Map<string, User>>(new Map());
  const [messages, setMessages] = useState<Message[]>(cached?.messages ?? []);
  const [unreadMessageCount, setUnreadMessageCount] = useState(cached?.unreadMessageCount ?? 0);
  const [referralCount, setReferralCount] = useState(cached?.referralCount ?? 0);
  const [groupChats, setGroupChats] = useState<GroupChat[]>(cached?.groupChats ?? []);
  const [groupMembers, setGroupMembers] = useState<GroupMember[]>(cached?.groupMembers ?? []);
  const [groupMessages, setGroupMessages] = useState<GroupMessage[]>(cached?.groupMessages ?? []);
  const [conversationDeletions, setConversationDeletions] = useState<ConversationDeletion[]>(cached?.conversationDeletions ?? []);
  const [meetups, setMeetups] = useState<Meetup[]>([]);
  const [meetupRsvps, setMeetupRsvps] = useState<MeetupRsvp[]>([]);
  const [loadError, setLoadError] = useState(false);

  // Sync currentUser when authUser changes (e.g. profile refresh from AuthContext)
  useEffect(() => {
    if (authUser) setCurrentUser(authUser);
  }, [authUser]);

  // Refs for realtime callback access (avoid stale closures)
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const groupMembersRef = useRef(groupMembers);
  groupMembersRef.current = groupMembers;

  const [month, setMonthState] = useState(() => new Date().getMonth() + 1);
  const [year, setYearState] = useState(() => new Date().getFullYear());

  // Track whether we have loaded data at least once (stale data is better than no data)
  const hasLoadedOnce = useRef(!!cached);

  // Load all data from Supabase — wrapped in try/catch so network errors
  // don't crash the component tree. When offline we keep stale data.
  const loadData = useCallback(async () => {
    if (!currentUserId) return; // Guard against sign-out race
    try {
      setLoadError(false);
      const [friendsData, friendshipsData, pendingData] = await withTimeout(Promise.all([
        sb.getFriends(currentUserId),
        sb.getFriendships(currentUserId),
        sb.getPendingRequests(currentUserId),
      ]), LOAD_TIMEOUT_MS);

      setFriends(friendsData);
      setFriendships(friendshipsData);
      setPendingRequests(pendingData);

      // Build a user lookup map (friends + pending request senders)
      const userMap = new Map<string, User>();
      for (const f of friendsData) userMap.set(f.id, f);
      // Batch-fetch profiles for pending request senders not already known
      const unknownSenderIds = pendingData
        .map((p) => p.fromUserId)
        .filter((id) => id !== currentUserId && !userMap.has(id));
      if (unknownSenderIds.length > 0) {
        const senderProfiles = await sb.getProfilesBatch(unknownSenderIds);
        for (const profile of senderProfiles) {
          userMap.set(profile.id, profile);
        }
      }
      setUsersById(userMap);

      const friendIds = friendsData.map((f) => f.id);
      const [myFlights, accessibleFlights, messagesData, unreadCount, refCount, groupChatsData, groupMembersData, groupMessagesData, deletionsData, meetupsData, meetupRsvpsData] = await withTimeout(Promise.all([
        sb.getFlightsForUser(currentUserId),
        sb.getAllAccessibleFlights(currentUserId, friendIds),
        sb.getMessages(currentUserId),
        sb.getUnreadCount(currentUserId),
        sb.getReferralCount(currentUserId),
        sb.getGroupChats(currentUserId),
        sb.getGroupMembers(currentUserId),
        sb.getGroupMessages(currentUserId),
        sb.getConversationDeletions(currentUserId),
        sb.getMeetups(),
        sb.getMeetupRsvps(),
      ]), LOAD_TIMEOUT_MS);

      setFlights(myFlights);
      setAllFlights(accessibleFlights);
      setMessages(messagesData);
      setUnreadMessageCount(unreadCount);
      setReferralCount(refCount);
      setGroupChats(groupChatsData);
      setGroupMembers(groupMembersData);
      setGroupMessages(groupMessagesData);
      setConversationDeletions(deletionsData);
      setMeetups(meetupsData);
      setMeetupRsvps(meetupRsvpsData);
      hasLoadedOnce.current = true;

      // Write-through to offline cache
      const now = new Date().toISOString();
      setCachedAt(now);
      saveAppData(currentUserId, {
        currentUser: currentUser,
        flights: myFlights,
        allFlights: accessibleFlights,
        friends: friendsData,
        friendships: friendshipsData,
        pendingRequests: pendingData,
        messages: messagesData,
        unreadMessageCount: unreadCount,
        referralCount: refCount,
        groupChats: groupChatsData,
        groupMembers: groupMembersData,
        groupMessages: groupMessagesData,
        conversationDeletions: deletionsData,
      });
    } catch (err) {
      console.warn('[AppContext] loadData failed (offline?):', err);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [currentUserId]);

  // Initial data load
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Real-time subscriptions — handle errors/disconnects gracefully
  useEffect(() => {
    const channel = supabase
      .channel('app-changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'flights', filter: `user_id=eq.${currentUserId}` },
        () => { loadData(); }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'friendships' },
        () => { loadData(); }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'messages', filter: `receiver_id=eq.${currentUserId}` },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            const row = payload.new as {
              id: string; sender_id: string; receiver_id: string;
              content: string; read_at: string | null; edited_at: string | null; created_at: string;
            };
            // Skip our own messages — already handled via optimistic insert
            if (row.sender_id === currentUserId) return;
            const msg: Message = {
              id: row.id, senderId: row.sender_id, receiverId: row.receiver_id,
              content: row.content, readAt: row.read_at, editedAt: row.edited_at, createdAt: row.created_at,
            };
            setMessages((prev) => {
              if (prev.some((m) => m.id === msg.id)) return prev;
              return [...prev, msg];
            });
            setUnreadMessageCount((c) => c + 1);
          } else if (payload.eventType === 'UPDATE') {
            const row = payload.new as {
              id: string; sender_id: string; receiver_id: string;
              content: string; read_at: string | null; edited_at: string | null; created_at: string;
            };
            setMessages((prev) => prev.map((m) =>
              m.id === row.id
                ? { ...m, content: row.content, readAt: row.read_at, editedAt: row.edited_at }
                : m
            ));
          } else if (payload.eventType === 'DELETE') {
            const row = payload.old as { id: string };
            setMessages((prev) => prev.filter((m) => m.id !== row.id));
          }
        }
      )
      // Listen for updates on sent messages (read receipts from recipients)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'messages', filter: `sender_id=eq.${currentUserId}` },
        (payload) => {
          const row = payload.new as {
            id: string; content: string; read_at: string | null; edited_at: string | null;
          };
          setMessages((prev) => prev.map((m) =>
            m.id === row.id
              ? { ...m, content: row.content, readAt: row.read_at, editedAt: row.edited_at }
              : m
          ));
        }
      )
      // Group messages — realtime INSERT/UPDATE/DELETE (RLS filters to member groups)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'group_messages' },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            const row = payload.new as {
              id: string; group_id: string; sender_id: string;
              content: string; metadata: GroupMessageMetadata | null; edited_at: string | null; created_at: string;
            };
            // Skip own messages (already handled via optimistic insert)
            if (row.sender_id === currentUserId) return;
            // Only accept if we're a member of this group
            const isMember = groupMembersRef.current.some(
              (gm) => gm.groupId === row.group_id && gm.userId === currentUserId,
            );
            if (!isMember) return;
            const msg: GroupMessage = {
              id: row.id, groupId: row.group_id, senderId: row.sender_id,
              content: row.content, metadata: row.metadata ?? null, editedAt: row.edited_at, createdAt: row.created_at,
            };
            setGroupMessages((prev) => {
              if (prev.some((m) => m.id === msg.id)) return prev;
              return [...prev, msg];
            });
          } else if (payload.eventType === 'UPDATE') {
            const row = payload.new as {
              id: string; content: string; metadata: GroupMessageMetadata | null; edited_at: string | null;
            };
            setGroupMessages((prev) => prev.map((m) =>
              m.id === row.id ? { ...m, content: row.content, metadata: row.metadata ?? null, editedAt: row.edited_at } : m,
            ));
          } else if (payload.eventType === 'DELETE') {
            const row = payload.old as { id: string };
            setGroupMessages((prev) => prev.filter((m) => m.id !== row.id));
          }
        }
      )
      // Meetups — realtime changes
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'meetups' },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            const row = payload.new as { id: string; group_chat_id: string; created_by: string; city: string; date: string; time: string | null; place: string | null; activity: string | null; status: string; message_id: string | null; created_at: string };
            const meetup: Meetup = { id: row.id, groupChatId: row.group_chat_id, createdBy: row.created_by, city: row.city, date: row.date, time: row.time, place: row.place, activity: row.activity, status: row.status as Meetup['status'], messageId: row.message_id, createdAt: row.created_at };
            setMeetups((prev) => prev.some((m) => m.id === meetup.id) ? prev : [...prev, meetup]);
          } else if (payload.eventType === 'UPDATE') {
            const row = payload.new as { id: string; status: string };
            setMeetups((prev) => prev.map((m) => m.id === row.id ? { ...m, status: row.status as Meetup['status'] } : m));
          } else if (payload.eventType === 'DELETE') {
            const row = payload.old as { id: string };
            setMeetups((prev) => prev.filter((m) => m.id !== row.id));
          }
        }
      )
      // Meetup RSVPs — realtime changes
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'meetup_rsvps' },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            const row = payload.new as { id: string; meetup_id: string; user_id: string; status: string; auto_declined: boolean; responded_at: string | null };
            const rsvp: MeetupRsvp = { id: row.id, meetupId: row.meetup_id, userId: row.user_id, status: row.status as MeetupRsvp['status'], autoDeclined: row.auto_declined, respondedAt: row.responded_at };
            setMeetupRsvps((prev) => {
              // Dedup by meetupId+userId (not id) to handle optimistic inserts with temp IDs
              const idx = prev.findIndex((r) => r.meetupId === rsvp.meetupId && r.userId === rsvp.userId);
              if (idx >= 0) {
                const updated = [...prev];
                updated[idx] = rsvp; // Replace with real DB row (has correct id)
                return updated;
              }
              return [...prev, rsvp];
            });
          } else if (payload.eventType === 'UPDATE') {
            const row = payload.new as { id: string; status: string; auto_declined: boolean; responded_at: string | null };
            setMeetupRsvps((prev) => prev.map((r) => r.id === row.id ? { ...r, status: row.status as MeetupRsvp['status'], autoDeclined: row.auto_declined, respondedAt: row.responded_at } : r));
          } else if (payload.eventType === 'DELETE') {
            const row = payload.old as { id: string };
            setMeetupRsvps((prev) => prev.filter((r) => r.id !== row.id));
          }
        }
      )
      .subscribe((status, err) => {
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          console.warn('[AppContext] Real-time channel error:', status, err);
          // Don't crash — Supabase client will attempt to reconnect automatically.
        }
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [loadData]);

  // When the device comes back online, refresh data and let the real-time
  // channel reconnect (Supabase handles reconnection, but we re-fetch to
  // pick up anything we missed while offline).
  useEffect(() => {
    async function flushFriendRequestOutbox() {
      const queued = getFriendRequestOutbox();
      if (queued.length === 0) return;
      console.info(`[AppContext] Flushing ${queued.length} queued friend request(s)`);
      for (const req of queued) {
        try {
          const targetUser = await sb.getProfileByHandle(req.handle);
          if (targetUser && targetUser.id !== currentUserId) {
            await sb.sendFriendRequest(currentUserId, targetUser.id);
          }
        } catch (err) {
          console.warn(`[AppContext] Failed to flush friend request for ${req.handle}:`, err);
        }
      }
      clearFriendRequestOutbox();
    }

    function handleOnline() {
      console.info('[AppContext] Device back online — refreshing data');
      flushFriendRequestOutbox().catch(() => {});
      loadData();
    }

    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [loadData]);

  // Roster reminder — set up on mount, register tap listener
  useEffect(() => {
    setupRosterReminder().catch(() => {});
    registerReminderListener(() => {
      window.dispatchEvent(new CustomEvent('open-roster-import'));
    });
  }, []);

  // Share extension — store shared data in state (not DOM events) to avoid race conditions
  const [pendingSharedText, setPendingSharedText] = useState<string | null>(null);
  const [pendingSharedImage, setPendingSharedImage] = useState<ImageInput | null>(null);
  const clearPendingShare = useCallback(() => {
    setPendingSharedText(null);
    setPendingSharedImage(null);
  }, []);

  useEffect(() => {
    initShareExtensionListener();
    const cleanupText = onSharedRosterText((text) => {
      setPendingSharedText(text);
      // Also fire event for CalendarTab to open the modal
      window.dispatchEvent(new CustomEvent('shared-roster-text-ready', { detail: { text } }));
    });
    const cleanupImage = onSharedRosterImage((image) => {
      setPendingSharedImage(image);
      window.dispatchEvent(new CustomEvent('shared-roster-image-ready', { detail: image }));
    });
    return () => { cleanupText(); cleanupImage(); };
  }, []);

  // Overlap notification deep-link — navigate to overlaps tab on tap
  useEffect(() => {
    registerOverlapNotificationListener(() => {
      window.dispatchEvent(new CustomEvent('navigate-to-overlaps'));
    });
  }, []);

  // Visit monitoring — detect airport arrivals via CLVisit
  useEffect(() => {
    if (!currentUserId) return;
    let cleanup: (() => void) | null = null;
    initVisitMonitor(currentUserId).then((fn) => { cleanup = fn; }).catch(() => {});
    return () => { cleanup?.(); };
  }, [currentUserId]);

  // Push notifications — register with APNs after auth
  useEffect(() => {
    if (!currentUserId || loading) return;
    initPushNotifications(currentUserId).catch(() => {});
  }, [currentUserId, loading]);

  // Computed data
  const roster = useMemo(
    () => (currentUser ? buildRoster(flights, currentUser, month, year) : []),
    [flights, currentUser, month, year]
  );

  const overlaps = useMemo(
    () =>
      currentUser
        ? calculateOverlaps(currentUser, flights, friends, allFlights, month, year)
        : [],
    [currentUser, flights, friends, allFlights, month, year]
  );

  // Roster gating — past months are never gated
  const rosterActive = useMemo(
    () => isMonthInPast(month, year) || hasActiveRoster(flights, month, year),
    [flights, month, year],
  );

  // Premium gating
  const isPremium = useMemo(
    () => isPremiumActive(currentUser),
    [currentUser],
  );

  const premiumGatedOverlap = useCallback(
    (date: string) => isOverlapGatedByPremium(date, currentUser),
    [currentUser],
  );

  // Achievement tracking — detect newly unlocked badges for celebration
  const [celebratingAchievement, setCelebratingAchievement] = useState<Achievement | null>(null);
  const dismissCelebration = useCallback(() => setCelebratingAchievement(null), []);

  const currentAchievements = useMemo(
    () => {
      const stats = computeFlightStats(flights);
      return computeAchievements(stats, friends.length, flights, referralCount);
    },
    [flights, friends.length, referralCount],
  );

  // Compare with localStorage to detect new unlocks
  useEffect(() => {
    if (currentAchievements.length === 0 || !currentUserId) return;
    const storageKey = `rostfound_unlocked_${currentUserId}`;
    const prevRaw = localStorage.getItem(storageKey);
    const prev = prevRaw ? new Set(JSON.parse(prevRaw) as string[]) : new Set<string>();

    const unlocked = currentAchievements.filter((a) => a.unlocked);
    const unlockedIds = unlocked.map((a) => a.id);

    // Find first newly unlocked achievement (not in previous set)
    const newUnlock = unlocked.find((a) => !prev.has(a.id));

    // Always persist current state
    if (unlockedIds.length > 0 || prev.size > 0) {
      localStorage.setItem(storageKey, JSON.stringify(unlockedIds));
    }

    // Only celebrate if we had previous data (skip initial load population)
    if (newUnlock && prev.size > 0) {
      setCelebratingAchievement(newUnlock);
    }
  }, [currentAchievements, currentUserId]);

  // Notification state
  const [mutedVersion, setMutedVersion] = useState(0);
  const mutedFriendIds = useMemo(
    () => getMutedFriends(currentUserId),
    [currentUserId, mutedVersion]
  );

  const toggleFriendMute = useCallback(
    (friendId: string) => {
      toggleMute(currentUserId, friendId);
      setMutedVersion((v) => v + 1);
    },
    [currentUserId]
  );

  const {
    toasts,
    dismissToast,
    markAllSeen: markOverlapsSeen,
    unseenCount: unseenOverlapCount,
  } = useOverlapNotifications(overlaps, currentUserId, mutedFriendIds, rosterActive, currentUser.baseAirport ?? '');

  // Schedule overlap notifications when data changes
  useEffect(() => {
    if (!currentUser.baseAirport) return;
    scheduleOverlapNotifications(
      flights, overlaps, currentUser.baseAirport, rosterActive, mutedFriendIds, isPremium,
    ).catch(() => {});
  }, [flights, overlaps, currentUser.baseAirport, rosterActive, mutedFriendIds, isPremium]);

  // Actions
  const setMonth = useCallback((m: number, y: number) => {
    setMonthState(m);
    setYearState(y);
  }, []);

  const addFlight = useCallback(
    (flight: Flight) => {
      if (!requireOnline('add flight')) return;
      const { id: _id, ...rest } = flight;
      sb.addFlight(rest).then(() => loadData()).catch((err) => {
        console.warn('[AppContext] addFlight failed:', err);
      });
    },
    [loadData]
  );

  const addFlights = useCallback(
    async (flightList: Flight[]): Promise<AddFlightsResult> => {
      if (!requireOnline('import roster')) return { inserted: [], duplicatesSkipped: 0 };
      try {
        const rows = flightList.map(({ id: _id, ...rest }) => rest);
        const result = await sb.addFlights(rows);
        await loadData();
        // Mark roster uploaded for this month so the reminder doesn't fire
        markRosterUploaded().catch(() => {});
        // Notify friends about new overlaps (fire-and-forget)
        if (result.inserted.length > 0 && currentUserId) {
          notifyFriendsOfOverlaps(currentUserId, result.inserted).catch(() => {});
        }
        return result;
      } catch (err) {
        console.warn('[AppContext] addFlights failed:', err);
        return { inserted: [], duplicatesSkipped: 0 };
      }
    },
    [loadData, currentUserId]
  );

  const removeFlight = useCallback(
    (flightId: string) => {
      if (!requireOnline('remove flight')) return;
      sb.removeFlight(flightId).then(() => loadData()).catch((err) => {
        console.warn('[AppContext] removeFlight failed:', err);
      });
    },
    [loadData]
  );

  const updateFlight = useCallback(
    async (flightId: string, updates: Partial<Omit<Flight, 'id' | 'userId'>>) => {
      if (!requireOnline('update flight')) return;
      try {
        await sb.updateFlight(flightId, updates);
        await loadData();
      } catch (err) {
        console.warn('[AppContext] updateFlight failed:', err);
      }
    },
    [loadData]
  );

  const sendFriendRequest = useCallback(
    async (toHandle: string): Promise<string | null> => {
      if (!navigator.onLine) {
        queueFriendRequest(toHandle);
        window.dispatchEvent(new CustomEvent('offline-mutation', { detail: { action: 'send friend request', queued: true } }));
        return null; // null = success (queued)
      }
      try {
        const targetUser = await sb.getProfileByHandle(toHandle);
        if (!targetUser) return 'User not found';
        if (targetUser.id === currentUserId) return "That's you!";

        const result = await sb.sendFriendRequest(currentUserId, targetUser.id);
        if (!result) return 'Already connected or request pending';

        await loadData();
        return null;
      } catch (err) {
        console.warn('[AppContext] sendFriendRequest failed:', err);
        return 'Network error — check your connection';
      }
    },
    [currentUserId, loadData]
  );

  const acceptFriendRequest = useCallback(
    async (friendshipId: string) => {
      if (!requireOnline('accept friend request')) return;
      try {
        await sb.acceptFriendRequest(friendshipId);
        await loadData();
      } catch (err) {
        console.warn('[AppContext] acceptFriendRequest failed:', err);
      }
    },
    [loadData]
  );

  const rejectFriendRequest = useCallback(
    async (friendshipId: string) => {
      if (!requireOnline('reject friend request')) return;
      try {
        await sb.rejectFriendRequest(friendshipId);
        await loadData();
      } catch (err) {
        console.warn('[AppContext] rejectFriendRequest failed:', err);
      }
    },
    [loadData]
  );

  const removeFriend = useCallback(
    async (friendUserId: string) => {
      if (!requireOnline('remove friend')) return;
      try {
        await sb.removeFriend(currentUserId, friendUserId);
        await loadData();
      } catch (err) {
        console.warn('[AppContext] removeFriend failed:', err);
      }
    },
    [currentUserId, loadData]
  );

  const refreshData = useCallback(async () => {
    setLoading(true);
    await loadData();
  }, [loadData]);

  const sendMessage = useCallback(
    async (receiverId: string, content: string) => {
      if (!requireOnline('send message')) return;
      const tempId = crypto.randomUUID();
      const optimistic: Message = {
        id: tempId,
        senderId: currentUserId,
        receiverId,
        content,
        readAt: null,
        editedAt: null,
        createdAt: new Date().toISOString(),
      };
      // Optimistic insert
      setMessages((prev) => [...prev, optimistic]);
      try {
        const saved = await sb.sendMessage(currentUserId, receiverId, content);
        // Replace temp message with server response
        setMessages((prev) => prev.map((m) => (m.id === tempId ? saved : m)));
      } catch (err) {
        console.warn('[AppContext] sendMessage failed:', err);
        // Remove optimistic message on failure
        setMessages((prev) => prev.filter((m) => m.id !== tempId));
      }
    },
    [currentUserId]
  );

  const editMessage = useCallback(
    async (messageId: string, content: string) => {
      if (!requireOnline('edit message')) return;
      // Capture previous state for rollback
      let previous: Message | undefined;
      setMessages((prev) => {
        previous = prev.find((m) => m.id === messageId);
        return prev.map((m) =>
          m.id === messageId ? { ...m, content, editedAt: new Date().toISOString() } : m
        );
      });
      try {
        await sb.editMessage(messageId, content);
      } catch (err) {
        console.warn('[AppContext] editMessage failed:', err);
        if (previous) {
          setMessages((prev) =>
            prev.map((m) => (m.id === messageId ? previous! : m))
          );
        }
      }
    },
    []
  );

  const deleteMessage = useCallback(
    async (messageId: string) => {
      if (!requireOnline('delete message')) return;
      // Capture previous state for rollback
      let removed: Message | undefined;
      setMessages((prev) => {
        removed = prev.find((m) => m.id === messageId);
        return prev.filter((m) => m.id !== messageId);
      });
      try {
        await sb.deleteMessage(messageId);
      } catch (err) {
        console.warn('[AppContext] deleteMessage failed:', err);
        if (removed) {
          setMessages((prev) => [...prev, removed!].sort(
            (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          ));
        }
      }
    },
    []
  );

  const markConversationRead = useCallback(
    async (partnerId: string) => {
      try {
        await sb.markMessagesRead(currentUserId, partnerId);
        // Update local state
        setMessages((prev) =>
          prev.map((m) =>
            m.senderId === partnerId && m.receiverId === currentUserId && !m.readAt
              ? { ...m, readAt: new Date().toISOString() }
              : m
          )
        );
        // Recalculate unread count
        const newCount = await sb.getUnreadCount(currentUserId);
        setUnreadMessageCount(newCount);
      } catch (err) {
        console.warn('[AppContext] markConversationRead failed:', err);
      }
    },
    [currentUserId]
  );

  const createGroupChat = useCallback(
    async (name: string, memberIds: string[], cityIata?: string, rangeStart?: string, rangeEnd?: string): Promise<GroupChat | null> => {
      if (!requireOnline('create group chat')) return null;

      // Deduplicate: exact same member set → reuse (one group per unique set of friends)
      // Also finds groups where the user soft-deleted their membership and rejoins them
      const requestedSet = new Set([currentUserId, ...memberIds]);
      const existing = groupChats.find((g) => {
        // Check ALL members (including soft-deleted) to find matching groups
        const allMemberIds = new Set(
          groupMembers.filter((m) => m.groupId === g.id).map((m) => m.userId),
        );
        if (allMemberIds.size !== requestedSet.size) return false;
        for (const id of requestedSet) {
          if (!allMemberIds.has(id)) return false;
        }
        return true;
      });
      if (existing) {
        // Rejoin if user had soft-deleted their membership
        const myMembership = groupMembers.find(
          (m) => m.groupId === existing.id && m.userId === currentUserId,
        );
        if (myMembership?.deletedAt) {
          await sb.rejoinGroupChat(existing.id, currentUserId);
          const now = new Date().toISOString();
          setGroupMembers((prev) =>
            prev.map((m) =>
              m.groupId === existing.id && m.userId === currentUserId
                ? { ...m, deletedAt: null, joinedAt: now }
                : m,
            ),
          );
        }
        return existing;
      }

      try {
        const result = await sb.createGroupChat(name, memberIds, currentUserId, cityIata, rangeStart, rangeEnd);
        setGroupChats((prev) => [result.chat, ...prev]);
        setGroupMembers((prev) => [...prev, ...result.members]);
        return result.chat;
      } catch (err) {
        console.warn('[AppContext] createGroupChat failed:', err);
        return null;
      }
    },
    [currentUserId, groupChats, groupMembers],
  );

  const sendGroupMessage = useCallback(
    async (groupId: string, content: string, metadata?: MeetupProposalMetadata) => {
      if (!requireOnline('send group message')) return;
      const tempId = crypto.randomUUID();
      const optimistic: GroupMessage = {
        id: tempId,
        groupId,
        senderId: currentUserId,
        content,
        metadata: metadata ?? null,
        editedAt: null,
        createdAt: new Date().toISOString(),
      };
      setGroupMessages((prev) => [...prev, optimistic]);
      try {
        const saved = await sb.sendGroupMessage(groupId, currentUserId, content, metadata);
        setGroupMessages((prev) => prev.map((m) => (m.id === tempId ? saved : m)));
      } catch (err) {
        console.warn('[AppContext] sendGroupMessage failed:', err);
        setGroupMessages((prev) => prev.filter((m) => m.id !== tempId));
      }
    },
    [currentUserId],
  );

  const editGroupMessage = useCallback(
    async (messageId: string, content: string) => {
      if (!requireOnline('edit group message')) return;
      let previous: GroupMessage | undefined;
      setGroupMessages((prev) => {
        previous = prev.find((m) => m.id === messageId);
        return prev.map((m) =>
          m.id === messageId ? { ...m, content, editedAt: new Date().toISOString() } : m,
        );
      });
      try {
        await sb.editGroupMessage(messageId, content);
      } catch (err) {
        console.warn('[AppContext] editGroupMessage failed:', err);
        if (previous) {
          setGroupMessages((prev) => prev.map((m) => (m.id === messageId ? previous! : m)));
        }
      }
    },
    [],
  );

  const voteOnProposal = useCallback(
    async (messageId: string, vote: 'yes' | 'no') => {
      if (!requireOnline('vote on proposal')) return;
      // Optimistic update
      let previous: GroupMessage | undefined;
      setGroupMessages((prev) => {
        previous = prev.find((m) => m.id === messageId);
        return prev.map((m) => {
          if (m.id !== messageId || !m.metadata || m.metadata.type !== 'meetup_proposal') return m;
          return {
            ...m,
            metadata: { ...m.metadata, votes: { ...m.metadata.votes, [currentUserId]: vote } },
          };
        });
      });
      try {
        await sb.voteOnProposal(messageId, currentUserId, vote);
      } catch (err) {
        console.warn('[AppContext] voteOnProposal failed:', err);
        if (previous) {
          setGroupMessages((prev) => prev.map((m) => (m.id === messageId ? previous! : m)));
        }
      }
    },
    [currentUserId],
  );

  const deleteGroupMessage = useCallback(
    async (messageId: string) => {
      if (!requireOnline('delete group message')) return;
      let removed: GroupMessage | undefined;
      setGroupMessages((prev) => {
        removed = prev.find((m) => m.id === messageId);
        return prev.filter((m) => m.id !== messageId);
      });
      try {
        await sb.deleteGroupMessage(messageId);
      } catch (err) {
        console.warn('[AppContext] deleteGroupMessage failed:', err);
        if (removed) {
          setGroupMessages((prev) => [...prev, removed!].sort(
            (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
          ));
        }
      }
    },
    [],
  );

  const renameGroupChat = useCallback(
    async (groupId: string, name: string) => {
      if (!requireOnline('rename group chat')) return;
      setGroupChats((prev) => prev.map((gc) => gc.id === groupId ? { ...gc, name } : gc));
      try {
        await sb.renameGroupChat(groupId, name);
      } catch (err) {
        console.warn('[AppContext] renameGroupChat failed:', err);
        await loadData();
      }
    },
    [loadData],
  );

  const softDeleteGroupChat = useCallback(
    async (groupId: string) => {
      if (!requireOnline('leave group chat')) return;
      try {
        await sb.softDeleteGroupChat(groupId, currentUserId);
        // Optimistic: mark membership as deleted
        setGroupMembers((prev) => prev.map((gm) =>
          gm.groupId === groupId && gm.userId === currentUserId
            ? { ...gm, deletedAt: new Date().toISOString() }
            : gm,
        ));
        // Optimistic: decline all future meetup RSVPs in this group
        const groupMeetupIds = new Set(
          meetups.filter((m) => m.groupChatId === groupId && m.status !== 'cancelled').map((m) => m.id),
        );
        setMeetupRsvps((prev) => prev.map((r) =>
          r.userId === currentUserId && groupMeetupIds.has(r.meetupId) && r.status !== 'no'
            ? { ...r, status: 'no' as const, autoDeclined: true, respondedAt: new Date().toISOString() }
            : r,
        ));
      } catch (err) {
        console.warn('[AppContext] softDeleteGroupChat failed:', err);
      }
    },
    [currentUserId, meetups],
  );

  const createMeetup = useCallback(
    async (params: { groupChatId: string; city: string; date: string; time?: string; place?: string; activity?: string; messageId?: string; memberIds: string[] }): Promise<Meetup | null> => {
      if (!requireOnline('create meetup')) return null;
      try {
        const meetup = await sb.createMeetup({ ...params, createdBy: currentUserId });
        setMeetups((prev) => [...prev, meetup]);
        track('meetup_created', { city: params.city });
        // RSVPs are seeded by DB trigger (seed_meetup_rsvps) and arrive via realtime
        return meetup;
      } catch (err) {
        console.warn('[AppContext] createMeetup failed:', err);
        return null;
      }
    },
    [currentUserId],
  );

  const rsvpMeetup = useCallback(
    async (meetupId: string, vote: 'yes' | 'no') => {
      if (!requireOnline('rsvp meetup')) return;
      // Optimistic update
      let previous: MeetupRsvp | undefined;
      setMeetupRsvps((prev) => {
        const existing = prev.find((r) => r.meetupId === meetupId && r.userId === currentUserId);
        previous = existing;
        if (existing) {
          return prev.map((r) =>
            r.meetupId === meetupId && r.userId === currentUserId
              ? { ...r, status: vote, autoDeclined: false, respondedAt: new Date().toISOString() }
              : r
          );
        }
        return [...prev, { id: crypto.randomUUID(), meetupId, userId: currentUserId, status: vote, autoDeclined: false, respondedAt: new Date().toISOString() }];
      });
      try {
        await sb.rsvpMeetup(meetupId, currentUserId, vote);
      } catch (err) {
        console.warn('[AppContext] rsvpMeetup failed:', err);
        // Rollback
        if (previous) {
          setMeetupRsvps((prev) => prev.map((r) =>
            r.meetupId === meetupId && r.userId === currentUserId ? previous! : r
          ));
        }
      }
    },
    [currentUserId],
  );

  const cancelMeetup = useCallback(
    async (meetupId: string) => {
      if (!requireOnline('cancel meetup')) return;
      setMeetups((prev) => prev.map((m) => m.id === meetupId ? { ...m, status: 'cancelled' as const } : m));
      try {
        await sb.cancelMeetup(meetupId);
      } catch (err) {
        console.warn('[AppContext] cancelMeetup failed:', err);
        await loadData();
      }
    },
    [loadData],
  );

  const softDeleteDmConversation = useCallback(
    async (partnerId: string) => {
      if (!requireOnline('delete conversation')) return;
      try {
        await sb.softDeleteDmConversation(currentUserId, partnerId);
        const deletion: ConversationDeletion = {
          userId: currentUserId,
          partnerId,
          deletedAt: new Date().toISOString(),
        };
        setConversationDeletions((prev) => {
          const filtered = prev.filter((d) => d.partnerId !== partnerId);
          return [...filtered, deletion];
        });
      } catch (err) {
        console.warn('[AppContext] softDeleteDmConversation failed:', err);
      }
    },
    [currentUserId],
  );

  const updateCurrentUser = useCallback(
    async (updates: Partial<User>) => {
      if (!requireOnline('update profile')) return;
      try {
        await sb.updateProfile(currentUserId, updates);
        const updated = await sb.getProfile(currentUserId);
        if (updated) setCurrentUser(updated);
      } catch (err) {
        console.warn('[AppContext] updateCurrentUser failed:', err);
      }
    },
    [currentUserId]
  );

  const value: AppState = {
    loading,
    cachedAt,
    currentUser,
    month,
    year,
    flights,
    allFlights,
    roster,
    friends,
    friendships,
    pendingRequests,
    usersById,
    overlaps,
    messages,
    unreadMessageCount,
    sendMessage,
    editMessage,
    deleteMessage,
    markConversationRead,
    groupChats,
    groupMembers,
    groupMessages,
    conversationDeletions,
    createGroupChat,
    sendGroupMessage,
    editGroupMessage,
    deleteGroupMessage,
    voteOnProposal,
    renameGroupChat,
    softDeleteGroupChat,
    softDeleteDmConversation,
    meetups,
    meetupRsvps,
    createMeetup,
    rsvpMeetup,
    cancelMeetup,
    rosterActive,
    isPremium,
    premiumGatedOverlap,
    referralCount,
    celebratingAchievement,
    dismissCelebration,
    unseenOverlapCount,
    mutedFriendIds,
    toggleFriendMute,
    markOverlapsSeen,
    toasts,
    dismissToast,
    setMonth,
    addFlight,
    addFlights,
    removeFlight,
    updateFlight,
    sendFriendRequest,
    acceptFriendRequest,
    rejectFriendRequest,
    removeFriend,
    pendingSharedText,
    pendingSharedImage,
    clearPendingShare,
    loadError,
    refreshData,
    updateCurrentUser,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp(): AppState {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
}
